import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, Reveal } from "./motion-primitives";
import { useLang, richParts } from "@/i18n/LanguageContext";

const HERO_IMG = "/images/osrelis-31.png";

export const Hero = () => {
  const { c } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[740px] overflow-hidden bg-[#061114]">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="OSRELIS offshore installation vessel and jacket foundations" className="h-full w-full object-cover" style={{ filter: "saturate(0.6) contrast(1.05)" }} />
      </motion.div>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(3,12,15,.9) 0%, rgba(3,12,15,.6) 40%, rgba(3,12,15,.1) 80%), linear-gradient(0deg, rgba(3,12,15,.72), transparent 55%)",
        }}
      />
      <div className="hero-grid-lines absolute inset-0" />

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="absolute left-6 top-[24vh] z-[2] w-[min(880px,84vw)] md:left-[8vw]">
        <Reveal delay={0.1}>
          <p className="mb-7 font-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-mist" data-testid="hero-kicker">
            {c.hero.kicker}
          </p>
        </Reveal>
        <MaskedLines
          animate
          className="font-serif text-[clamp(52px,6.6vw,112px)] font-normal leading-[0.94] tracking-[-0.025em] text-paper"
          lines={c.hero.titleLines.map((l) => richParts(l))}
        />
        <Reveal delay={0.9}>
          <p className="mb-9 mt-8 max-w-[600px] font-sans text-[15px] leading-[1.7] text-paper/75 md:text-[17px]" data-testid="hero-intro">
            {c.hero.intro}
          </p>
        </Reveal>
        <Reveal delay={1.05}>
          <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:gap-9">
            <a
              href="#approach"
              data-testid="hero-cta-approach"
              className="group inline-flex items-center gap-8 bg-paper px-5 py-4 font-sans text-xs tracking-[0.04em] text-ink transition-colors duration-300 hover:bg-teal"
            >
              {c.hero.cta1}
              <span className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1">↗</span>
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="group inline-flex items-center gap-2 border-b border-paper pb-1 font-sans text-xs tracking-[0.04em] text-paper transition-colors duration-300 hover:border-teal hover:text-teal"
            >
              {c.hero.cta2}
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </a>
          </div>
        </Reveal>
      </motion.div>

      <Reveal delay={1.2} className="absolute bottom-11 right-[4vw] z-[2] hidden w-[43%] border-t border-paper/25 pt-4 md:block">
        <div className="grid grid-cols-3">
          {c.hero.index.map((it, i) => (
            <div key={i} className="pr-4">
              <span className="font-sans text-[10px] text-paper/50">{`0${i + 1}`}</span>
              <p className="mt-1 font-sans text-[11px] leading-[1.5] text-paper/75">
                {it.l1}
                <br />
                {it.l2}
              </p>
            </div>
          ))}
        </div>
      </Reveal>

      <a href="#about" className="absolute bottom-10 left-6 z-[2] flex items-center gap-3 font-sans text-[9px] uppercase tracking-[0.2em] text-paper/65 md:left-[4vw]" data-testid="scroll-cue">
        <span className="block h-px w-9 bg-paper/50" />
        {c.hero.scroll}
      </a>
    </section>
  );
};
